<template>
  <card :title="sessionType" v-loading="loading.playground">
    <el-form label-position="left" label-width="200px" size="small">
      <el-form-item label="ID">
        <el-input v-model="payload.id" disabled />
      </el-form-item>
      <el-form-item label="Name">
        <el-input v-model="payload.name" disabled />
      </el-form-item>
      <el-form-item label="ZIP file" v-if="isZipGeneration && payload.target_url">
        <a :href="payload.target_url" target="_blank" rel="noopener noreferrer">
          Open ZIP
        </a>
      </el-form-item>
      <template v-if="!isZipGeneration">
        <el-form-item label="Session Id" v-if="payload.session_id">
          <a :href="`https://dev.animaapp.com/chat/${payload.session_id}`" target="_blank">
            {{ payload.session_id }}
          </a>
        </el-form-item>
        <el-form-item label="DB Url" v-if="dbUrl">
          <a :href="dbUrl" target="_blank">
            {{ dbUrl }}
          </a>
        </el-form-item>
        <el-form-item label="Design File URL" v-if="payload.design_file_url">
          <a :href="payload.design_file_url" target="_blank">
            {{ payload.design_file_url }}
          </a>
        </el-form-item>
        <el-form-item label="Live URL" v-if="payload.live_url">
          <a :href="payload.live_url" target="_blank">
            {{ payload.live_url }}
          </a>
        </el-form-item>
        <el-form-item label="Subdomain" v-if="payload.subdomain">
          <el-input v-model="payload.subdomain" disabled />
        </el-form-item>
        <el-form-item label="Code Files" v-if="payload.target_url">
          <a :href="payload.target_url" target="_blank">
            {{ payload.target_url }}
          </a>
        </el-form-item>
        <el-form-item label="File Key" v-if="payload.file_key">
          <el-input v-model="payload.file_key" disabled />
        </el-form-item>
        <el-form-item label="Custom Domains" v-if="payload.custom_hostnames && payload.custom_hostnames.length">
          <el-tag v-for="hostname in payload.custom_hostnames" :key="hostname" style="margin-right: 5px;">
            <a :href="`https://${hostname}`" target="_blank">
              {{ hostname }}
            </a>
          </el-tag>
        </el-form-item>
      </template>
      <el-form-item label="Created at">
        <div class="block">
          <el-date-picker v-model="payload.created_at" type="datetime" disabled>
          </el-date-picker>
        </div>
      </el-form-item>
      <el-form-item label="Updated at">
        <div class="block">
          <el-date-picker v-model="payload.updated_at" type="datetime" disabled>
          </el-date-picker>
        </div>
      </el-form-item>
      <el-form-item label="Owner" v-if="ownerDetails.email">
        <router-link :to="`/customers/user/${ownerDetails.email}`">
          {{ ownerDetails.email }}
        </router-link>
      </el-form-item>
      <el-form-item label="Owner ID" v-else>
        <el-input v-model="payload.owner" disabled />
      </el-form-item>
      <el-form-item label="Team" v-if="teamDetails.name">
        <router-link :to="`/customers/team/${teamDetails.id}`">
          {{ teamDetails.name }}
        </router-link>
      </el-form-item>
      <el-form-item label="Team ID" v-else>
        <el-input v-model="payload.team" disabled />
      </el-form-item>
      <template v-if="!isZipGeneration">
        <el-form-item label="Sandbox Type">
          <el-radio-group v-model="sandboxType">
            <el-radio label="inherit">Inherit</el-radio>
            <el-radio label="csb">Sandpack</el-radio>
            <el-radio label="webcontainer">Web Container</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Abuse">
          <el-button v-if="payload.live_url" @click="unpublishPlayground" type="warning">
            Unpublish
          </el-button>
          <el-button @click="takedownPlayground" type="danger">Takedown</el-button>
          <el-button @click="whitelistAbuseScan" type="info">Whitelist scan</el-button>
          <el-button @click="removeAbuseScanWhitelist">Remove scan whitelist</el-button>
        </el-form-item>
        <el-form-item>
          <el-button @click="save" type="primary">Save</el-button>
        </el-form-item>
      </template>
    </el-form>
  </card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Card from '@/components/Card';
import { generationSessionType, isZipGenerationSession } from '@/utils/generationSessions';

export default {
  components: {
    Card,
  },
  data() {
    return {
      payload: {},
      ownerDetails: {},
      teamDetails: {},
      sandboxType: 'inherit',
    };
  },
  computed: {
    ...mapGetters(['loading', 'playground']),
    isZipGeneration() {
      return isZipGenerationSession(this.payload);
    },
    sessionType() {
      return generationSessionType(this.payload);
    },
    dbUrl() {
      const sessionId = this.payload.session_id || this.payload.id;
      if (!sessionId) return null;

      // Convert session_id to hex
      let hex = '';
      for (let i = 0; i < sessionId.length; i++) {
        hex += sessionId.charCodeAt(i).toString(16);
      }

      return `https://app.turso.tech/anima/databases/playground-${hex}`;
    },
  },
  watch: {
    playground: {
      handler(playground) {
        this.payload = { ...playground };
        this.sandboxType = playground.sandbox_type || 'inherit';
        // Fetch owner and team details if available
        if (playground.owner) {
          this.fetchOwnerDetails(playground.owner);
        }
        if (playground.team) {
          this.fetchTeamDetails(playground.team);
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['getUser', 'getTeam', 'updatePlayground']),
    async fetchOwnerDetails(ownerId) {
      try {
        await this.getUser(ownerId);
        this.ownerDetails = this.$store.getters.user;
      } catch (e) {
        console.error('Could not fetch owner details', e);
      }
    },
    async fetchTeamDetails(teamId) {
      try {
        await this.getTeam(teamId);
        this.teamDetails = this.$store.getters.team;
      } catch (e) {
        console.error('Could not fetch team details', e);
      }
    },
    async save() {
      try {
        const payload = {
          id: this.payload.id,
          sandbox_type: this.sandboxType === 'inherit' ? null : this.sandboxType,
        };
        await this.updatePlayground(payload);
        this.$message.success('Saved successfully');
      } catch (e) {
        this.$notify.error({
          title: 'Save failed',
          message: e.message,
        });
      }
    },
    unpublishPlayground() {
      this.$confirm(
        'Stop serving the published site. Assets stay in S3 and the playground keeps its current privacy. Continue?',
        'Unpublish playground',
        {
          confirmButtonText: 'Yes, unpublish',
          cancelButtonText: 'Cancel',
          type: 'warning',
        }
      )
        .then(async () => {
          try {
            const { data } = await this.$api.post(`/cs/playgrounds/${this.payload.id}/unpublish`);
            this.$message.success(data.message || 'Unpublish queued');
          } catch (e) {
            console.error(e);
            this.$message.error('Failed to unpublish playground');
          }
        })
        .catch(() => {});
    },
    takedownPlayground() {
      this.$confirm(
        'Zip dist assets, wipe them from S3, clear CloudFront cache, and mark the playground private. A recovery zip is posted to #abuse. Continue?',
        'Takedown playground',
        {
          confirmButtonText: 'Yes, takedown',
          cancelButtonText: 'Cancel',
          type: 'error',
        }
      )
        .then(async () => {
          try {
            const { data } = await this.$api.post(`/cs/playgrounds/${this.payload.id}/takedown`);
            this.$message.success(data.message || 'Takedown queued');
          } catch (e) {
            console.error(e);
            this.$message.error('Failed to takedown playground');
          }
        })
        .catch(() => {});
    },
    async whitelistAbuseScan() {
      try {
        const { value: reason } = await this.$prompt(
          'Reason (required) — recorded for audit.',
          'Whitelist session from abuse scan',
          {
            confirmButtonText: 'Whitelist',
            cancelButtonText: 'Cancel',
            inputValidator: (v) => (v && v.trim().length > 0) || 'Reason is required',
            inputType: 'textarea',
          }
        );
        const sessionId = this.payload.session_id || this.payload.id;
        await this.$api.patch(`/abuse-scan/${sessionId}/whitelist`, {
          whitelisted: true,
          reason: reason.trim(),
        });
        this.$message.success('Session whitelisted from abuse scan');
      } catch (e) {
        if (e === 'cancel') return;
        this.$message.error(e?.response?.data?.error || 'Failed to whitelist session');
      }
    },
    removeAbuseScanWhitelist() {
      this.$confirm('Re-enable abuse scanning for this session?', 'Remove whitelist', {
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        type: 'warning',
      })
        .then(async () => {
          try {
            const sessionId = this.payload.session_id || this.payload.id;
            await this.$api.patch(`/abuse-scan/${sessionId}/whitelist`, { whitelisted: false });
            this.$message.success('Whitelist removed');
          } catch (e) {
            this.$message.error(e?.response?.data?.error || 'Failed to remove whitelist');
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
</style>
